/**
 * GiveCardPickerModal.tsx — R19-GC Policy_Exchange give-arm picker.
 *
 * Shown after the StealCard arm of Policy_Exchange fires — drawer picks one
 * card from their own hand to give to the same opponent (completing the SWAP).
 *
 * Hand-full forfeit: if opponent's hand is already at MAX_HAND_SIZE, the give
 * step is skipped automatically by commitGiveCard (no card transferred).
 * This modal still shows so the player knows the full SWAP attempted.
 *
 * Pattern mirrors EnergyDilemmaModal: latch + boardAnimationActive gate.
 *
 * VISIBLE WHEN: pendingGiveCard !== null AND boardAnimationActive === false
 * TEST PATH: era 3 → play Policy_Exchange → pick opponent → StealCard fires
 *            → GiveCardPickerModal renders → drawer picks a card to give.
 */

import { MAX_HAND_SIZE } from "@gdg/rules-engine";
import type React from "react";
import { useEffect, useState } from "react";
import { type SeatIndex, getCardById, useGameStore } from "../../store/game-store.js";
import styles from "./GiveCardPickerModal.module.css";

export function GiveCardPickerModal(): React.ReactElement | null {
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const pendingGiveCard = useGameStore((s) => s.pendingGiveCard);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const hands = useGameStore((s) => s.hands);
  const commitGiveCard = useGameStore((s) => s.commitGiveCard);
  const cancelGiveCard = useGameStore((s) => s.cancelGiveCard);

  // Latch so the modal survives pendingGiveCard being cleared.
  const [activeGive, setActiveGive] = useState<{
    victimSeat: SeatIndex;
    sourceCardId: string;
  } | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);

  useEffect(() => {
    if (pendingGiveCard !== null && activeGive === null) {
      setActiveGive({
        victimSeat: pendingGiveCard.victimSeat,
        sourceCardId: pendingGiveCard.sourceCardId,
      });
      setSelectedInstanceId(null);
    }
    // Clear latch when pendingGiveCard resolves.
    if (pendingGiveCard === null && activeGive !== null) {
      setActiveGive(null);
      setSelectedInstanceId(null);
    }
  }, [pendingGiveCard, activeGive]);

  if (boardAnimationActive) return null;
  if (activeGive === null) return null;

  const drawerHand = hands[currentSeat] ?? [];
  const recipientHand = hands[activeGive.victimSeat] ?? [];
  const recipientFull = recipientHand.length >= MAX_HAND_SIZE;
  const canConfirm = selectedInstanceId !== null && !recipientFull;

  function handleConfirm() {
    if (selectedInstanceId === null) return;
    commitGiveCard(selectedInstanceId);
  }

  function handleSkip() {
    cancelGiveCard();
  }

  return (
    <div className={styles.overlay} data-testid="give-card-picker-overlay">
      <div className={styles.panel} data-testid="give-card-picker-panel">
        <h2 className={styles.title}>POLICY EXCHANGE</h2>
        <p className={styles.subtitle} data-testid="give-card-picker-subtitle">
          You stole a card from P{activeGive.victimSeat + 1}.
          {recipientFull
            ? " Their hand is full — give step is forfeit."
            : ` Now give one of your cards to P${activeGive.victimSeat + 1}.`}
        </p>

        {!recipientFull && drawerHand.length > 0 ? (
          <div className={styles.cardGrid} data-testid="give-card-grid">
            {drawerHand.map((c) => {
              const def = getCardById(c.id);
              const isSelected = selectedInstanceId === c.instanceId;
              return (
                <button
                  key={c.instanceId}
                  type="button"
                  className={`${styles.cardSlot} ${isSelected ? styles.cardSlotSelected : ""}`}
                  onClick={() => setSelectedInstanceId(isSelected ? null : c.instanceId)}
                  data-testid={`give-card-slot-${c.instanceId}`}
                  aria-pressed={isSelected}
                >
                  <span className={styles.cardName}>{def?.cardName ?? c.id}</span>
                  {def?.cardType !== undefined && (
                    <span className={styles.cardType}>{def.cardType}</span>
                  )}
                </button>
              );
            })}
          </div>
        ) : !recipientFull ? (
          <p className={styles.emptyHand} data-testid="give-card-empty-hand">
            No cards in hand to give.
          </p>
        ) : null}

        <div className={styles.footer}>
          {!recipientFull && (
            <button
              type="button"
              className={styles.confirmBtn}
              onClick={handleConfirm}
              disabled={!canConfirm}
              data-testid="give-card-confirm"
            >
              GIVE CARD
            </button>
          )}
          <button
            type="button"
            className={styles.skipBtn}
            onClick={handleSkip}
            data-testid="give-card-skip"
          >
            {recipientFull ? "Continue" : "Forfeit Give"}
          </button>
        </div>
      </div>
    </div>
  );
}
